import Settings from '../domain/Settings';
import Slot, {SlotStateError} from '../domain/Slot';
import arrayUtils from '../utils/array-util';
import * as nodeUtils from '../utils/node';
import perf from '../utils/performance';
import pubsub from '../utils/pubsub';
import * as adManager from './adManager';
import breakpoints from './breakpoints';
import lazyLoadService from './lazyLoadService';
import sizeMappings from './sizeMappings';

const DEFAULT_SLOT_LOAD_BATCH_COUNT = 12;

let slots = [],
	loadSlotBatchCount,
	waitingForDefine;

_hookEvents();

export async function init(settings) {
	perf.mark('xandr - slotmanager.init - start');

	loadSlotBatchCount = settings.loadSlotBatchCount || DEFAULT_SLOT_LOAD_BATCH_COUNT;
	waitingForDefine = {};

	_removeSlots();
	_createSlots(settings.slots);

	slots.forEach((s) => {
		adManager.addRenderFinishListener(s, (adObject) => _onRenderFinish(s, adObject));
		adManager.addAvailableListeners(s, (adObject) => _onAdAvailable(s, adObject));
		adManager.addRequestedListeners(s, () => _onAdRequestedEvent(s), (error, ...params) => _onAdRequestFailureEvent(s, error, ...params));
		adManager.addMediationListeners(s, (filled) => _onMediationEvent(s, filled));
	});

	await defineSlots(slots.filter((slot) => slot.getPreload()));

	perf.mark('xandr - slotmanager.init - end');
}

function _hookEvents() {
	pubsub.subscribe('breakpoint.changed', _onBreakpointChange);
	pubsub.subscribe('slot.defined', _onSlotDefined);
}

function _xandrErrorToSlotError(xandrError) {
	switch (xandrError) {
		case 'adRequestFailure':
			return SlotStateError.REQUEST_FAILURE;

		case 'adBadRequest':
			return SlotStateError.BAD_PLACEMENT;
	}
}

function _onBreakpointChange(currentBreakpoint) {
	const slotsToRefresh = slots
		.filter((/** Slot*/ slot) => slot.state.showTagCalled)
		.filter((/** Slot*/ slot) => slot.renderBreakpoint && slot.renderBreakpoint !== currentBreakpoint);

	adManager.updatePageTargeting();

	return refreshSlots(slotsToRefresh, 'breakpoint_changed');
}

function _onRenderFinish(slot, adObject) {
	slot.setAdData(adObject);
	slot.setRendered(true);
}

function _onAdAvailable(slot, adObject) {
	slot.setAdData(adObject);
	slot.setReceived(true);
}

function _onMediationEvent(slot, empty) {
	slot.setMediated(true, empty);
}

function _onAdRequestedEvent(slot) {
	slot.setRequested(true);
}

function _onAdRequestFailureEvent(slot, error, ...params) {
	const errorType = _xandrErrorToSlotError(error);

	slot.setError(errorType);

	console.error('[ADVERT] Could not load slot', slot, ' | Error type:', errorType, ' | Error data:', ...params);
}

function _isUsedOnCurrentBreakpoint(slot) {
	const sizes = sizeMappings.getSizesFromSizeMapForBreakpoint(slot.sizeMapping, breakpoints.getCurrentBreakpoint());

	return typeof sizes !== 'undefined' && sizes.length > 0;
}

function _createSlots(settingSlots) {
	slots = settingSlots.reduce((agg, s) => {
		try {
			agg.push(new Slot(s));
		} catch (e) {
			console.error('[ADVERT] Could not create slot. Error:', e);
		}

		return agg;
	}, []);
}

function _removeSlots() {
	slots.forEach((slot) => {
		adManager.removeRenderFinishListener(slot);
		adManager.removeAvailableListeners(slot);
		adManager.removeMediationListeners(slot);
		adManager.removeRequestedListeners(slot);
		slot.removeNode();
	});

	slots = [];
}

export async function defineSlots(toDefine) {
	perf.mark('xandr - slotmanager.define - start');

	const slotsToDefine = toDefine
		.filter(_isUsedOnCurrentBreakpoint);

	slotsToDefine.forEach((slot) => {
		adManager.defineSlot(slot);
		slot.setDefined(true);
	});

	perf.mark('xandr - slotmanager.extendSlots - start');
	await pubsub.publish('extendSlots', slotsToDefine);
	perf.mark('xandr - slotmanager.extendSlots - end');

	_fetchSlots(slotsToDefine);

	perf.mark('xandr - slotmanager.define - end');
}

function _fetchSlots(definedSlots) {
	let batchCount;

	if (typeof loadSlotBatchCount === 'object') {
		batchCount = loadSlotBatchCount[breakpoints.getCurrentBreakpoint()];
	} else {
		batchCount = loadSlotBatchCount;
	}

	const batches = arrayUtils.getBatched(definedSlots, typeof batchCount === 'number' ? batchCount : DEFAULT_SLOT_LOAD_BATCH_COUNT);

	batches.map((batch) => adManager.loadSlots(batch));
}

async function _waitForSlotDefine(slot) {
	if (slot.state.defined) {
		return;
	}

	// It should define a non-preloaded slot, but no others as those are likely broken or busy.
	if (slot.getPreload()) {
		await new Promise((resolve) => {
			waitingForDefine[slot.name] = resolve;
		});
	} else {
		await defineSlots([slot]);
	}
}

function _onSlotDefined(slot) {
	if (typeof waitingForDefine[slot.name] === 'function') {
		waitingForDefine[slot.name]();
		delete waitingForDefine[slot.name];
	}
}

export function refreshSlots(slotsToRefresh, reason) {
	slotsToRefresh.forEach((slot) => {
		if (typeof reason !== 'undefined') {
			slot.targeting.refresh = reason;
		}

		slot.setError(null);
		slot.setMediated(false);
	});

	adManager.refreshSlots(slotsToRefresh);
}

export function getSlotNameFromMapping(platformName) {
	const slotNameMappings = Settings.getInstance().slotNameMappings;

	if (!slotNameMappings) {
		return platformName;
	}

	const mapping = slotNameMappings[platformName];

	if (typeof mapping === 'undefined') {
		throw new Error(`[ADVERT] Unknown slot '${platformName}'`);
	}

	if (typeof mapping === 'string' || mapping === null) {
		return mapping;
	}

	const currentBp = breakpoints.getCurrentBreakpoint();

	// Can be null to not render
	return typeof mapping[currentBp] === 'undefined' ? mapping.default : mapping[currentBp];
}

export async function loadSlot(name, nodeId = name) {
	const slotName = getSlotNameFromMapping(name);

	// Intentionally unmapped slot, not an error
	if (slotName === null) {
		return;
	}

	const slot = slots.find((s) => s.name === slotName);

	if (!slot) {
		throw new Error(`No slot found for ${name}`);
	}

	const node = document.getElementById(nodeId);

	if (!node) {
		// Slot already loaded for this node
		if (slot.node && slot.node.getAttribute('data-advert-orig-id') === nodeId) {
			return;
		}

		slot.setError(SlotStateError.NODE_NOT_FOUND);

		throw new Error(`No DOM element found for id ${nodeId}`);
	}

	if (slot.node === node) {
		return;
	}

	slot.updateNode(node);

	await _waitForSlotDefine(slot);

	if (slot.lazyLoad) {
		await lazyLoadService.waitForSlot(slot);
	}

	if (!nodeUtils.isConnected(node)) {
		console.warn('[ADVERT] Tried rendering slot in a node that\'s no longer connected to the DOM', slot, node);

		return;
	}

	if (slot.state.showTagCalled) {
		refreshSlots([slot]);
	} else {
		adManager.renderSlot(slot);
		slot.setShowTagCalled(true);
	}

	return slot;
}

export function getSlot(slotName) {
	return slots.find((s) => s.name === slotName);
}

export function getSlots() {
	return [...slots];
}
